import React, { useState, useMemo, useEffect } from "react";
import {
  Plus,
  Megaphone,
} from "lucide-react";
import { 
  useAdvertisementsFindQuery, 
  useAdvertisementsDelete,
  useAdvertisementsBulkDelete
} from "@/lib/api/requests/advertisements.requests";
import { AdvertisementListResponseDto } from "@/lib/api/interfaces/advertisements.interface";
import { toast } from "@/lib/toast";
import CreateAdvertisementDialog from "./create-dialog";
import EditAdvertisementDialog from "./edit-dialog";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import {
  DataView,
  DataViewToolbar,
  DataViewLayout,
  DataViewContent,
  DataViewLoadingState,
  DataViewEmptyState,
  DataViewLoadMore,
  DataViewGridCard,
  DataViewListCard,
  GenericItem,
  GenericItemActions
} from "@/util-components/data-view";
import { PageInfoBanner, PrimaryActionButton } from "@/util-components/page-layout";
import { usePageState } from "@/lib/hooks/use-page-state";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";

/**
 * Anzahl der Leistungen pro Seite
 */
const DEFAULT_PAGE_SIZE = 18;

/**
 * Hilfsfunktionen für Formatierung
 */
const formatEuro = (value: number) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);

const unitLabelFromCode = (code: string) => {
  switch (code) {
    case 'DAY': return 'Tag';
    case 'WEEK': return 'Woche';
    case 'MONTH': return 'Monat';
    case 'YEAR': return 'Jahr';
    default: return code;
  }
};

const pluralizeUnit = (count: number, unit: string) => {
  if (count === 1) return unit;
  if (unit === 'Woche') return 'Wochen';
  if (unit === 'Monat') return 'Monate';
  if (unit === 'Jahr') return 'Jahre';
  if (unit === 'Tag') return 'Tage';
  return unit;
};

const AdvertisementsWrapper = () => {
  return <Advertisements />;
};

const Advertisements: React.FC = () => {
  const { state, setInfoBannerVisible, setViewMode } = usePageState('sponsors-advertisements', {
    isInfoBannerVisible: true,
    viewMode: 'grid'
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [allAdvertisements, setAllAdvertisements] = useState<AdvertisementListResponseDto[]>([]);
  
  const viewMode = state.viewMode || 'grid';

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [advertisementToDelete, setAdvertisementToDelete] = useState<AdvertisementListResponseDto | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [advertisementToEdit, setAdvertisementToEdit] = useState<AdvertisementListResponseDto | null>(null);

  const { mutate: deleteAdvertisement, isPending: isDeleting } = useAdvertisementsDelete();
  const { mutate: bulkDeleteAdvertisements, isPending: isBulkDeleting } = useAdvertisementsBulkDelete();

  // API-Filterparameter
  const filterParams = useMemo(
    () => ({
      textSearch: searchTerm.trim() || undefined,
      page: currentPage,
      limit: DEFAULT_PAGE_SIZE,
    }),
    [searchTerm, currentPage],
  );

  // API-Hooks
  const advertisementsQuery = useAdvertisementsFindQuery(filterParams);

  // Update advertisements when data changes
  useEffect(() => {
    if (advertisementsQuery.data?.docs) {
      if (currentPage === 1) {
        setAllAdvertisements(advertisementsQuery.data.docs);
      } else {
        setAllAdvertisements(prev => {
          const newDocs = advertisementsQuery.data?.docs || [];
          const existingIds = new Set(prev.map(a => a._id));
          const filteredNewDocs = newDocs.filter(d => !existingIds.has(d._id));
          return [...prev, ...filteredNewDocs];
        });
      }
    }
  }, [advertisementsQuery.data, currentPage]);

  const hasMore = advertisementsQuery.data?.hasNextPage || false;
  const isInitialLoading = advertisementsQuery.isLoading && currentPage === 1;
  const isLoadingMore = advertisementsQuery.isLoading && currentPage > 1;

  const handleDeleteConfirm = () => {
    if (isBulkDelete && selectedIds.length > 0) {
      bulkDeleteAdvertisements({ ids: selectedIds }, {
        onSuccess: () => {
          toast.success(`${selectedIds.length} Leistungen erfolgreich gelöscht`);
          setAllAdvertisements(prev => prev.filter(a => !selectedIds.includes(a._id)));
          setShowDeleteDialog(false);
          setAdvertisementToDelete(null);
          setIsBulkDelete(false);
          setSelectedIds([]);
          setCurrentPage(1);
        },
        onError: (error) => {
          toast.error("Leistungen konnten nicht gelöscht werden", { error });
        },
      });
      return;
    }

    if (!advertisementToDelete) return;
    
    deleteAdvertisement({ id: advertisementToDelete._id }, {
      onSuccess: () => {
        toast.success("Leistung erfolgreich gelöscht");
        setAllAdvertisements(prev => prev.filter(a => a._id !== advertisementToDelete._id));
        setShowDeleteDialog(false);
        setAdvertisementToDelete(null);
        setCurrentPage(1);
      },
      onError: (error) => {
        toast.error("Leistung konnte nicht gelöscht werden", { error });
      },
    });
  };

  const handleSelectionDelete = () => {
    if (selectedIds.length > 0) {
      setAdvertisementToDelete(null);
      setIsBulkDelete(true);
      setShowDeleteDialog(true);
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setAllAdvertisements([]);
  };

  const handleViewModeChange = (newViewMode: 'grid' | 'list') => {
    setViewMode(newViewMode);
  };

  const convertToGenericItem = (adv: AdvertisementListResponseDto): GenericItem => {
    let periodLabel = "Einmalzahlung";
    if (adv.duration !== undefined && adv.interval !== undefined) {
      const unitLabel = unitLabelFromCode(String(adv.interval));
      const unitPlural = pluralizeUnit(adv.duration, unitLabel);
      periodLabel = `${adv.duration} ${unitPlural}`;
    }

    const typeLabel = adv.type === "DONATION" ? "Spende" : "Sponsoring";

    return {
      id: adv._id,
      title: adv.name,
      description: `${typeLabel} • ${formatEuro(adv.volume)} • ${periodLabel} • Max. gleichzeitig: ${adv.max_concurrent_allowed ?? 'keine Beschränkung'}${adv.show_on_social_media ? ' • Social Media' : ''}`,
      previewContent: (
        <div className='w-full h-full flex items-center justify-center overflow-hidden rounded-md bg-blue-50'>
          <Megaphone className="h-6 w-6 text-blue-600" />
        </div>
      ),
      previewContentList: (
        <div className='w-full h-full flex items-center justify-center overflow-hidden rounded-md bg-blue-50'>
          <Megaphone className="h-6 w-6 text-blue-600" />
        </div>
      )
    };
  };

  const genericItems = useMemo(() => {
    return allAdvertisements.map(convertToGenericItem);
  }, [allAdvertisements]);

  const genericActions: GenericItemActions = {
    onItemClick: (id: string) => {
      const adv = allAdvertisements.find(a => a._id === id) || null;
      setAdvertisementToEdit(adv);
      setEditDialogOpen(!!adv);
    },
    onEdit: (id: string) => {
      const adv = allAdvertisements.find(a => a._id === id) || null;
      setAdvertisementToEdit(adv);
      setEditDialogOpen(!!adv);
    },
    onDelete: (id: string) => {
      const adv = allAdvertisements.find(a => a._id === id);
      if (adv) {
        setAdvertisementToDelete(adv);
        setIsBulkDelete(false);
        setShowDeleteDialog(true);
      }
    }
  };

  return (
    <DataView 
      viewMode={viewMode} 
      onViewModeChange={handleViewModeChange}
      isLoading={isInitialLoading}
      isEmpty={allAdvertisements.length === 0}
      selectedIds={selectedIds}
      onMultiSelect={setSelectedIds}
      onSelectionDelete={handleSelectionDelete}
    >
      <div className="bg-white flex-shrink-0 border-b border-gray-100">
        {state.isInfoBannerVisible && (
          <div className="p-4 pb-0">
            <PageInfoBanner
              title="Werbeleistungen definieren"
              description={
                <>
                  Erstellen Sie einen Katalog an Werbemöglichkeiten (z.B. Trikotsponsoring, Bandenwerbung), die Sie Ihren Sponsoren anbieten. <br/>
                  Diese Leistungen können anschließend konkreten Partnern zugewiesen werden.
                </>
              }
              icon={Megaphone}
              variant="blue"
              onClose={() => setInfoBannerVisible(false)}
            />
          </div>
        )}
        
        <DataViewToolbar
          searchQuery={searchTerm}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Leistungen suchen..."
          infoText={`${advertisementsQuery.data?.count || 0} Leistungen`}
          leading={
            <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
              <PrimaryActionButton
                  onClick={() => setCreateDialogOpen(true)}
                  label="Leistung hinzufügen"
                  icon={Plus}
              />
            </PermissionFeatureWrapper>
          }
        />
      </div>

      <DataViewLayout>
        <DataViewContent
           items={genericItems}
           renderGridItem={(item) => <DataViewGridCard item={item} actions={genericActions} />}
           renderListItem={(item) => <DataViewListCard item={item} actions={genericActions} />}
           loadingIndicator={<DataViewLoadingState />}
           emptyState={
             <DataViewEmptyState
               icon={<Megaphone className="h-8 w-8 text-gray-400" />}
               title={searchTerm.trim() !== "" ? 'Keine Leistungen gefunden' : 'Noch keine Leistungen vorhanden'}
               description={searchTerm.trim() !== "" 
                 ? 'Versuchen Sie andere Suchbegriffe.'
                 : 'Legen Sie Ihre erste Leistung an, um sie Sponsoren zuweisen zu können.'
               }
               action={
                searchTerm.trim() === "" ? (
                  <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                    <PrimaryActionButton 
                      onClick={() => setCreateDialogOpen(true)} 
                      label="Leistung hinzufügen"
                      icon={Plus}
                    />
                  </PermissionFeatureWrapper>
                ) : undefined
              }
             />
           }
        />

        <DataViewLoadMore
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
        />
      </DataViewLayout>

      {createDialogOpen && (
        <CreateAdvertisementDialog
          open={createDialogOpen}
          onClose={(refetchData) => {
            setCreateDialogOpen(false);
            if (refetchData) {
              setCurrentPage(1);
            }
          }}
        />
      )}

      {editDialogOpen && (
        <EditAdvertisementDialog
          open={editDialogOpen}
          advertisement={advertisementToEdit}
          onClose={(updated) => {
            setEditDialogOpen(false);
            setAdvertisementToEdit(null);
            if (updated) {
              setCurrentPage(1);
            }
          }}
        />
      )}

      {showDeleteDialog && (advertisementToDelete || isBulkDelete) && (
        <StandardDeleteDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            title={isBulkDelete ? `${selectedIds.length} Leistungen löschen` : "Leistung löschen"}
            description={isBulkDelete ? "Möchten Sie diese Leistungen wirklich unwiderruflich löschen?" : "Diese Aktion kann nicht rückgängig gemacht werden"}
            itemTitle={advertisementToDelete?.name}
            itemTypeLabel={advertisementToDelete ? "ID" : undefined}
            itemTypeValue={advertisementToDelete?._id}
            itemsList={isBulkDelete ? selectedIds.map(id => {
              const a = allAdvertisements.find(ad => ad._id === id);
              return {
                title: a ? a.name : id
              };
            }) : undefined}
            onDelete={async () => handleDeleteConfirm()}
            isLoading={isDeleting || isBulkDeleting}
        />
      )}
    </DataView>
  );
};

export default AdvertisementsWrapper;
